import { inject, Injectable } from "@angular/core";
import { CancelFuturesOrderResult, CancelOrderParams, FuturesCancelAlgoOrderParams, FuturesCancelAlgoOrderResponse, FuturesNewAlgoOrderParams, NewFuturesOrderParams } from "libs/binance-api/src";
import { forkJoin, from, Observable } from "rxjs";
import { parseFloatSafe, parseIntSafe } from "src/app/core/func";
import { BinanceUsdmRestClient } from "src/app/core/market";
import { CreateOrderParams, CreateStopOrderParams, Order, OrderSide, OrderType, PositionSide } from "src/app/core/model/trade";
import { OrderHelper } from "../../order.helper";
import { chunk } from "lodash-es";

@Injectable({
  providedIn: 'root'
})
export class BinanceUsdmOrderManager {

  private orderHelper = inject(OrderHelper)

  createOrder(rest: BinanceUsdmRestClient, order: CreateOrderParams) {
    const params: NewFuturesOrderParams = {
      newClientOrderId: this.orderHelper.generateClientOrderId(order.market, order.board),
      symbol: order.symbol,
      side: order.side == OrderSide.buy ? 'BUY' : 'SELL',
      type: order.type == OrderType.market ? 'MARKET' : 'LIMIT',
      quantity: parseFloatSafe(order.quantity),
    }
    if (order.price !== undefined) {
      params.price = parseFloatSafe(order.price)
    }
    if (order.positionSide !== undefined) {
      params.positionSide = order.positionSide == PositionSide.both ? 'BOTH' : order.positionSide == PositionSide.long ? 'LONG' : 'SHORT'
    }
    if (order.type == OrderType.limit) {
      params.timeInForce = 'GTC'
    }
    if (order.closePosition === true) {
      params.reduceOnly = 'true'
    }
    return from(rest.submitNewOrder(params))
  }

  createStopOrder(rest: BinanceUsdmRestClient, order: CreateStopOrderParams) {
    const params: FuturesNewAlgoOrderParams = {
      clientAlgoId: this.orderHelper.generateClientOrderId(order.market, order.board),
      algoType: 'CONDITIONAL',
      symbol: order.symbol,
      side: order.side == OrderSide.buy ? 'BUY' : 'SELL',
      type: order.type == OrderType.market ? 'STOP_MARKET' : 'STOP',
      triggerPrice: parseFloatSafe(order.triggerPrice),
    }
    if (order.quantity !== undefined) {
      params.quantity = parseFloatSafe(order.quantity)
    }
    if (order.price !== undefined) {
      params.price = parseFloatSafe(order.price)
    }
    if (order.positionSide !== undefined) {
      params.positionSide = order.positionSide == PositionSide.both ? 'BOTH' : order.positionSide == PositionSide.long ? 'LONG' : 'SHORT'
    }
    if (order.closePosition === true) {
      if (order.quantity === undefined) {
        params.closePosition = 'true'
      } else {
        params.reduceOnly = 'true'
      }
    }
    return from(rest.submitNewAlgoOrder(params))
  }

  cancelOrder(rest: BinanceUsdmRestClient, order: Order): Observable<FuturesCancelAlgoOrderResponse | CancelFuturesOrderResult> {
    if (order.isStop) {
      const params: FuturesCancelAlgoOrderParams = {
        algoId: parseIntSafe(order.orderId)
      }
      return from(rest.cancelAlgoOrder(params))
    }

    const params: CancelOrderParams = {
      symbol: order.symbol
    }
    if (order.clientOrderId) {
      params.origClientOrderId = order.clientOrderId
    } else {
      params.orderId = parseIntSafe(order.orderId)
    }
    return from(rest.cancelOrder(params))
  }

  cancelOrders(rest: BinanceUsdmRestClient, orders: Order[]) {
    // Разбиваем заявки по инструментам
    const ordersMap = new Map<string, number[]>()
    orders.forEach(order => {
      const arr = ordersMap.get(order.symbol) ?? []
      arr.push(parseIntSafe(order.orderId))
      ordersMap.set(order.symbol, arr)
    })

    // Разбиваем на запросы по 10 заявок
    const requests: Observable<unknown>[] = []
    ordersMap.forEach((orders, symbol) => {
      requests.push(
        ...chunk(orders, 10).map(batch => from(rest.cancelMultipleOrders({symbol, orderIdList: batch})))
      )
    })

    return forkJoin(requests)
  }

}
